import { useState } from 'react'
import { router, usePage } from '@inertiajs/react'
import Layout, { Empty } from '@/components/Layout'
import { Badge, Banner, Button, Chip, Field, List, Row, Section, Segments, fieldInputClass } from '@/components/ui'
import DateStrip, { type StripDay } from '@/components/DateStrip'
import ProgressRing from '@/components/ProgressRing'
import ReviewQueue from '@/components/ReviewQueue'
import AttachmentPicker, { type Attachment } from '@/components/AttachmentPicker'
import AudioList from '@/components/AudioList'
import PhotoStrip from '@/components/PhotoStrip'
import PhotoViewer from '@/components/PhotoViewer'
import { Sheet, SheetPad } from '@/components/Sheet'
import { toast } from '@/components/Toast'
import type { SharedProps } from '@/types'
import { shrinkImage } from '@/lib/shrinkImage'
import { subjectEmoji } from './subjects'
import { t } from '@/lib/i18n'

// 稿子（docs/designs/mobile/app-homework.html）里的「📷 拍黑板」已经落地（S6 Task 4）：
// ActiveStorage 的表在 S6 装上了，dev/test/prod 三个 service 与 Kamal 的持久卷
// 早就配好了 —— 原来那句「缺 ActiveStorage 所以落不了地」不成立。
//
// **录音那半仍然不做**（S6 决定 D）：它要 getUserMedia + MediaRecorder + 局域网
// http 下的兜底文案，是独立一片。所以这里一律 accept="image/*"。

export interface HomeworkItemRow {
  id: number
  subject: string
  position: number
  body: string
  kind: 'todo' | 'note'
  points: number
  needs_signature: boolean
  due_on: string | null
  state: 'open' | 'pending' | 'done'
  /** 交了没有。**独立于 state** —— 书读完了不等于书交了（S6 决定 E）。 */
  handed: boolean
  on: string
  signed_by: string | null
  /** 打卡证据（稿子的 `hw:<日期>:<条目>`）。撤销打卡不删它。 */
  proofs: Attachment[]
}

interface Props {
  date_label: string
  viewed_date: string
  max_date: string
  is_today: boolean
  recorded: boolean
  source: string | null
  posted_at: string | null
  items: HomeworkItemRow[]
  /** 这一天的作业图（拍黑板）。稿子的 `hwshot:<日期>`。 */
  shots: Attachment[]
  due_items: HomeworkItemRow[]
  sign_items: HomeworkItemRow[]
  /** 每天 { 做完几件, 一共几件 } —— 日期条圆点要三态，只给 done 数分不出「没录」。 */
  day_counts: Record<string, { done: number; total: number }>
  earned_today: number
  cap: number | null
  capped: boolean
  settings: { points_per: number; daily_cap: number | null; standing: string }
  can_write: boolean
  can_claim_only: boolean
}

type Seg = 'today' | 'due' | 'sign'
const POINTS = [0, 1, 2, 3, 5, 10]

export default function Index({
  date_label, viewed_date, max_date, is_today, recorded, items, due_items, sign_items,
  day_counts, earned_today, cap, capped, settings, can_write, can_claim_only, shots,
  source, posted_at,
}: Props) {
  const locale = usePage<SharedProps>().props.locale
  const [seg, setSeg] = useState<Seg>('today')
  const [detail, setDetail] = useState<HomeworkItemRow | null>(null)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [addOpen, setAddOpen] = useState(false)
  const [shotSheet, setShotSheet] = useState(false)
  // 看哪张照片。**必须带上是哪一种** —— 天级作业图和条目级证据是两条不同的
  // 删除路由，只存 Photo 的话删除只能猜一个，猜错的那一半会静默失败（踩过）。
  const [viewing, setViewing] = useState<{ photo: Attachment; kind: 'shot' | 'proof' } | null>(null)
  const [uploading, setUploading] = useState(false)
  // 给哪一条加附件。null = 面板关着。
  //
  // **存 id 而不是整条**：存整条的话它是一个快照 —— 在面板里删掉一段录音之后
  // props 刷新了，而快照里那一条还在，面板上就还挂着一个已经不存在的附件。
  // 存 id、每次渲染从 items 里取，props 一变面板跟着变。踩过。
  const [proofForId, setProofForId] = useState<number | null>(null)

  // 「打卡」这个动作对孩子（只有 claim 权限）也开：作业的「我做完了」就是申报，
  // 需签名的条目会停在等家长签。改条目 / 删条目 / 签名只给 write。
  const canAct = can_write || can_claim_only

  const proofFor = proofForId === null ? null : items.find((i) => i.id === proofForId) ?? null

  const todos = items.filter((i) => i.kind === 'todo')
  const doneCount = todos.filter((i) => i.state === 'done').length

  // ── 写操作 ──
  const complete = (item: HomeworkItemRow) => {
    router.post(`/homework_items/${item.id}/complete`, {}, {
      preserveScroll: true,
      onSuccess: () => toast(
        // 需签名的停在 pending、到上限的记完成但不加分 —— 三种结果说三种话。
        // 结果由刷新后的 props 体现，不用把服务端返回值传出来。
        item.needs_signature
          ? t('homework.toast_pending', { body: item.body })
          : capped
            ? t('homework.toast_capped', { cap: cap ?? 0 })
            : t('homework.toast_done', { body: item.body, count: item.points }),
        {
          undo: () => undo(item),
          // 稿子的 toast 第二动作位：刚打完卡那一刻是最可能想拍一张的时候
          alt: canAct ? { label: `📷 ${t('homework.proof_add')}`, fn: () => setProofForId(item.id) } : undefined,
        },
      ),
    })
  }

  const undo = (item: HomeworkItemRow) => {
    router.delete(`/homework_items/${item.id}/complete`, {
      preserveScroll: true,
      onSuccess: () => toast(t('homework.toast_undone', { body: item.body })),
    })
  }

  const sign = (item: HomeworkItemRow) => {
    router.post(`/homework_items/${item.id}/sign`, {}, {
      preserveScroll: true,
      onSuccess: () => toast(t('homework.toast_signed', { body: item.body })),
    })
  }

  // 「✓ 交了」：不动 state、不动积分，只是把这一条从「要交」那一段划掉。
  const hand = (item: HomeworkItemRow) => {
    router.post(`/homework_items/${item.id}/hand`, {}, {
      preserveScroll: true,
      onSuccess: () => toast(
        t('homework.toast_handed', { body: item.body }),
        { undo: () => router.delete(`/homework_items/${item.id}/hand`, { preserveScroll: true }) },
      ),
    })
  }

  // ── 作业图（拍黑板）──
  const uploadShots = async (files: File[]) => {
    setUploading(true)
    // 压缩是逐张的异步活儿，**全压完再一次 POST** —— 一张一个请求会让 Inertia
    // 连着刷新四遍页面，而且中间那几帧的照片条会闪。
    const small = await Promise.all(files.map((f) => shrinkImage(f)))
    router.post('/homework/photos', { date: viewed_date, images: small }, {
      forceFormData: true,
      preserveScroll: true,
      onSuccess: () => {
        setShotSheet(false)
        toast(t('homework.toast_photo_added', { count: small.length }))
      },
      onFinish: () => setUploading(false),
    })
  }

  const uploadProofs = (files: File[]) => {
    const item = proofFor
    if (!item) return

    setUploading(true)
    // **原样上传，不压缩**（照片和录音都是）。这条路径的照片是「拍给爸妈看」的
    // 留念 —— 压到 480px / q=0.72 之后爸妈一放大就是一团糊，而那正是这张照片
    // 唯一的用途。上行慢一点是可以接受的代价，画质糊掉不是。
    //
    // 与 uploadShots 刻意不同：天级作业图（拍老师写的黑板）照旧压，那边
    // 480px 足够「看清老师写了什么」，且一天能拍好几张。
    //
    // ⚠️ 服务端 HomeworkProofsController::MAX_IMAGE_BYTES 必须装得下手机原图
    // （现在是 12MB）。谁把那个数调回 2MB，这里的原图会被静默拒掉 ——
    // 用户看到的是「这张没上去」，不是「这张被压了」。
    router.post(`/homework_items/${item.id}/proofs`, { images: files }, {
      forceFormData: true,
      preserveScroll: true,
      onSuccess: () => {
        // 刻意**不关面板**：刚加完常常还想再加一个、或者看一眼加对了没。
        // 面板的内容现在是从 items 派生的，props 一刷新它自己就更新了。
        toast(t('homework.toast_photo_added', { count: files.length }))
      },
      onFinish: () => setUploading(false),
    })
  }

  // 两条路由：天级作业图 /homework/photos/:id，条目级证据 /homework_proofs/:id。
  // 两个控制器的作用域各自只认自己那种 attachment，所以路由选错 = 静默不删。
  // 音频从列表里直接删（它没有「看大图」这一步，所以不经查看器）。
  // 与照片删除刻意分开：那一条要先关查看器、且带二次确认。
  const deleteProof = (audio: Attachment) => {
    router.delete(`/homework_proofs/${audio.id}`, {
      preserveScroll: true,
      onSuccess: () => toast(t('homework.toast_audio_deleted')),
    })
  }

  const deleteAttachment = (photo: Attachment) => {
    const kind = viewing?.kind
    setViewing(null)
    router.delete(kind === 'proof' ? `/homework_proofs/${photo.id}` : `/homework/photos/${photo.id}`, {
      preserveScroll: true,
      onSuccess: () => toast(t('homework.toast_photo_deleted')),
    })
  }

  // ── 日期条 ──
  // 星期短名交给 Intl：自己拼「周一/周二」会把语序和语言锁死在中文上。
  const weekday = new Intl.DateTimeFormat(locale, { weekday: 'short' })
  const days: StripDay[] = Object.keys(day_counts).sort().map((date) => {
    // 'YYYY-MM-DD' 直接进 new Date() 会被当成 UTC 午夜，在东八区显示成前一天。
    const d = new Date(`${date}T00:00`)
    const { done, total } = day_counts[date]
    return {
      date,
      label: date === max_date ? t('checkin.strip_today') : weekday.format(d),
      day: d.getDate(),
      count: done,
      // 录了但一件没做完 —— 琥珀色的半成品态，和「压根没录」区分开
      partial: done === 0 && total > 0,
    }
  })

  // 稿子的 dueText 五档。星期短名交给 Intl —— 自己拼「周四」会把语序锁死在中文上，
  // 而 zh-CN 的 short weekday 输出的已经是「周四」（所以 i18n 那边没有「周」前缀）。
  const dueText = (iso: string) => {
    const today = new Date(`${max_date}T00:00`)
    const target = new Date(`${iso}T00:00`)
    const diff = Math.round((target.getTime() - today.getTime()) / 86400000)
    if (diff <= 0) return t('homework.due_today')
    if (diff === 1) return t('homework.due_tomorrow')
    if (diff === 2) return t('homework.due_day_after')
    // 7 天整落在同一个星期几，说「周四」会和本周撞车，所以 <7 才说「周四」
    if (diff < 7) return t('homework.due_weekday', { day: weekday.format(target) })
    if (diff < 14) return t('homework.due_next_weekday', { day: weekday.format(target) })
    return t('homework.due_date', { date: iso.slice(5) })
  }

  // 距离要交的日子还有几天。≤1 天在分组头上变琥珀（稿子的 `.soon`）。
  const daysLeft = (iso: string) =>
    Math.round((new Date(`${iso}T00:00`).getTime() - new Date(`${max_date}T00:00`).getTime()) / 86400000)

  // 稿子的 `.srcchip`：「老师 18:40 发的」——让家长能和群里那条消息对上。
  // source 与 posted_at 服务端一直在算、Props 里一直声明着，但之前没人渲染。
  const recordedAt = posted_at
    ? new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' }).format(new Date(posted_at))
    : ''

  // 按学科分组。用 Map 而不是普通对象：对象的数字 key 会被 JS 重排，
  // 而学科顺序要跟着服务端给的（老师那张单子的顺序）。
  const groups = new Map<string, HomeworkItemRow[]>()
  items.forEach((i) => groups.set(i.subject, [...(groups.get(i.subject) ?? []), i]))

  // 「加一条」的学科 chips：当天已有的（按老师那张单子的顺序）+ 三个常见的
  // + 「其它」，去重。学科在库里是自由文本，这几个只是省打字用的候选。
  const COMMON_SUBJECTS = ['语文', '数学', '英语']
  const subjectChoices = [...new Set([
    ...items.map((i) => i.subject),
    ...COMMON_SUBJECTS,
    t('homework.add_subject_other'),
  ])]

  const detailButton = (item: HomeworkItemRow) => can_write ? (
    <button
      type="button"
      onClick={() => setDetail(item)}
      aria-label={t('homework.detail_aria', { body: item.body })}
      aria-haspopup="dialog"
      className="shrink-0 min-w-tap min-h-tap rounded-chip text-muted text-lg active:bg-sunk"
    >
      ⋯
    </button>
  ) : undefined

  // 稿子的 `.noterow`：左侧一条竖色条 + 「要求 · 不计分」tag。
  //
  // 刻意**不是** `.row` —— 它不可点、没有分值、不进进度。长得像一行可打卡的
  // 东西就是在骗人：老师那句「回家作业注意书写格式：」根本没有「完成」的时刻
  // （设计稿 §四 第 1 条）。
  const noteRow = (item: HomeworkItemRow) => (
    <div key={item.id} className="flex gap-2.5 px-3.5 py-3 border-b border-line last:border-b-0">
      <span aria-hidden="true" className="shrink-0 w-[3px] rounded-full bg-amber-strong self-stretch" />
      <span className="min-w-0 text-[13px] leading-[1.5] text-muted">
        <span className="inline-block mr-1.5 px-2 py-0.5 rounded-full bg-amber-tint text-amber-strong text-[11px] font-bold align-[1px]">
          {t('homework.note_hint')}
        </span>
        {item.body}
      </span>
    </div>
  )

  // 一行作业。「今天」段用它；「要交」段有自己的一套（那一段整行点是跳转，不是打卡）。
  const itemRow = (item: HomeworkItemRow, showSubject = false) => {
    if (item.kind === 'note') return noteRow(item)

    return (
      <Row
        key={item.id}
        lead={
          <span className={
            'inline-flex w-[26px] h-[26px] rounded-full items-center justify-center text-xs font-extrabold ' +
            (item.state === 'done' ? 'bg-green-tint text-green' : 'bg-sunk text-muted')
          }>
            {item.state === 'done' ? '✓' : item.position}
          </span>
        }
        // 已完成**留在原位划掉**，不像习惯那样抽走折叠：编号要和老师那张单子对得上
        done={item.state === 'done'}
        title={item.body}
        sub={
          <span className="flex items-center gap-1.5 flex-wrap">
            {showSubject && <span>{subjectEmoji(item.subject)} {item.subject}</span>}
            {/* 截止徽标**打完卡也留着** —— 「下周四回收」没有因为今天读完而消失 */}
            {item.due_on && <Badge tone="brand">🗓 {dueText(item.due_on)}</Badge>}
            {item.needs_signature && item.state === 'pending' && <Badge tone="amber">{t('homework.awaiting_sign')}</Badge>}
            {item.needs_signature && item.state !== 'pending' && <Badge tone="amber">{t('homework.needs_sign')}</Badge>}
            {/* 稿子的 `.attline`：**已完成行的整个行体是附件入口**，它自己的 CSS 注释
                写的就是「点整块而不是点一个小图标」。所以这两个是**纯文本标签**、
                不是按钮 —— 做成按钮它们在 sub 里只有 16px 高，破规矩 §1
                （44px 下限，无例外）。实测量过。可点的是整行（≥64px），见下面的 onTap。 */}
            {item.proofs.some((a) => a.kind === 'photo') && (
              <span className="text-brand font-bold">
                📷 {item.proofs.filter((a) => a.kind === 'photo').length}
              </span>
            )}
            {item.proofs.some((a) => a.kind === 'audio') && (
              <span className="text-brand font-bold">
                🎙️ {item.proofs.filter((a) => a.kind === 'audio').length}
              </span>
            )}
            {item.state === 'done' && item.proofs.length === 0 && canAct && (
              <span className="text-brand font-bold">＋ {t('homework.proof_show_parents')}</span>
            )}
          </span>
        }
        pts={`+${item.points}`}
        // 三个状态三种整行动作，照稿子：
        //   open    → 打卡
        //   pending → 撤销（它在等家长签，孩子能做的只有反悔）
        //   done    → 开附件面板（稿子的 `.attline`：整块可点，撤销走右边那个按钮）
        onTap={
          !canAct ? undefined
            : item.state === 'open' ? () => complete(item)
              : item.state === 'pending' ? () => undo(item)
                : () => setProofForId(item.id)
        }
        ariaLabel={
          item.state === 'done'
            ? t('homework.proof_aria', { body: item.body })
            : t('homework.row_aria', { body: item.body, count: item.points })
        }
        right={
          // 三个按钮属于三个**互斥**状态（pending / open / done），最多同时出现两个
          // （它自己那一个 + ⋯）。375px 上够：44 + 44 + 内容区。
          <span className="flex shrink-0 gap-1">
            {item.state === 'pending' && can_write && (
              <button
                type="button"
                onClick={() => sign(item)}
                aria-label={t('homework.sign_one_aria', { body: item.body, count: item.points })}
                className="min-h-tap px-3 rounded-chip bg-green-tint text-green-strong text-xs font-bold active:bg-green active:text-white"
              >
                ✍️ {t('homework.sign_one')}
              </button>
            )}
            {/* 稿子这里是「打卡**并**加照片」两个动作。这里只开面板、**不打卡** ——
                一个按钮做两件事在真实数据上很难撤销（打卡成功但上传失败时状态对不上）。
                加完照片行还在那儿等着点。 */}
            {item.state === 'open' && canAct && (
              <button
                type="button"
                onClick={() => setProofForId(item.id)}
                aria-label={t('homework.proof_aria', { body: item.body })}
                className="shrink-0 min-w-tap min-h-tap rounded-chip text-muted text-base active:bg-sunk"
              >
                📷
              </button>
            )}
            {/* 稿子的 done 行有一个写着「撤销」的按钮。整行再点一次也能撤，
                但那个动作猜不出来 —— 划掉的行看着像「已经结束了」。 */}
            {item.state === 'done' && canAct && (
              <button
                type="button"
                onClick={() => undo(item)}
                aria-label={t('homework.undo_aria', { body: item.body })}
                className="shrink-0 min-h-tap px-2.5 rounded-chip text-muted text-xs font-semibold active:bg-sunk"
              >
                {t('homework.undo')}
              </button>
            )}
            {detailButton(item)}
          </span>
        }
      />
    )
  }

  return (
    <Layout
      bleed
      topbar={{
        back: '/checkin',
        title: t('homework.title'),
        // 稿子的作业页顶栏是混合形态：返回 + 人员切换 + 余额（不是纯二级页的标题）
        showPerson: true,
        right: can_write ? (
          <button
            type="button"
            onClick={() => setSettingsOpen(true)}
            aria-label={t('homework.settings_title')}
            aria-haspopup="dialog"
            className="min-w-tap min-h-tap flex items-center justify-center text-lg text-muted"
          >
            ⚙️
          </button>
        ) : undefined,
      }}
    >
      <DateStrip
        days={days}
        selected={viewed_date}
        onSelect={(date) => router.get('/homework', { date }, { preserveScroll: true })}
      />

      {!is_today && (
        <Banner tone="amber" icon="🗓️">{t('homework.backfill_banner', { date: date_label })}</Banner>
      )}

      {/* 稿子的 `.shots`：作业图在 hero 之前、日期条之后。跨天两段不该看到它
          —— 那两段是跨天汇总，而图是挂在某一天上的。 */}
      {seg === 'today' && (
        <PhotoStrip
          photos={shots}
          canWrite={can_write}
          onAdd={() => setShotSheet(true)}
          onOpen={(photo) => setViewing({ photo, kind: 'shot' })}
        />
      )}

      <div className="mx-3 mt-3">
        <Segments<Seg>
          label={t('homework.seg_label')}
          value={seg}
          onChange={setSeg}
          options={[
            { value: 'today', label: `${t('homework.seg_today')} ${todos.length - doneCount}` },
            { value: 'due', label: `${t('homework.seg_due')} ${due_items.length}` },
            { value: 'sign', label: `${t('homework.seg_sign')} ${sign_items.length}` },
          ]}
        />
      </div>

      {seg === 'today' && (
        recorded ? (
          <>
            <section className="mx-3 mt-3 p-4 bg-card rounded-card shadow-card flex items-center gap-4">
              <ProgressRing done={doneCount} total={todos.length} />
              <div className="min-w-0">
                <h1 className="m-0 mb-[3px] text-[17px] font-bold">
                  {todos.length - doneCount > 0
                    ? t('homework.hero_left', { count: todos.length - doneCount })
                    : t('homework.hero_all_done')}
                </h1>
                <p className="m-0 text-muted text-[13px]">{t('homework.hero_earned', { count: earned_today })}</p>
                {/* 到上限**如实说**，不悄悄少给（设计稿 §五） */}
                {capped && (
                  <span className="inline-flex items-center gap-1 mt-[7px] px-[9px] py-[3px] rounded-full bg-amber-tint text-amber-strong text-xs font-bold">
                    {t('homework.capped', { cap: cap ?? 0 })}
                  </span>
                )}
                {/* 这张单子是怎么、什么时候进来的。t() 的 key 必须是字面量
                    （i18n_client_keys_test 扫的就是字面量），所以三个分支各写一次。 */}
                {posted_at && (
                  <span className="inline-flex items-center gap-1 mt-[7px] ml-1.5 px-[9px] py-[3px] rounded-full bg-sunk text-faint text-xs font-semibold">
                    {source === 'photo'
                      ? t('homework.src_photo', { time: recordedAt })
                      : source === 'manual'
                        ? t('homework.src_manual', { time: recordedAt })
                        : t('homework.src_paste', { time: recordedAt })}
                  </span>
                )}
              </div>
            </section>

            {[...groups].map(([subject, list]) => {
              const todosIn = list.filter((i) => i.kind === 'todo')
              const doneIn = todosIn.filter((i) => i.state === 'done')
              const earnedIn = doneIn.reduce((sum, i) => sum + i.points, 0)
              return (
                <Section
                  key={subject}
                  title={<>{subjectEmoji(subject)} {subject}</>}
                  // 稿子的 `.tail`：3/5 · +6 分。只有条数（「5 条」）说不出进度
                  tail={<>{doneIn.length}/{todosIn.length}{earnedIn > 0 && ` · +${earnedIn}`}</>}
                >
                  <List>{list.map((item) => itemRow(item))}</List>
                </Section>
              )
            })}

            {can_write && (
              <>
                {/* 两个 50px 按钮 + 8px 间距 + 18px 余量。不垫够的话最后一行作业
                    会被这两个固定按钮压住。 */}
                <div className="h-[126px]" />
                <div className="fixed left-3 right-3 z-[35] above-tabbar max-w-[536px] mx-auto grid gap-2">
                  <Button variant="ghost" onClick={() => router.post('/homework/preview', { text: '', date: viewed_date })}>
                    📋 {t('homework.add_more')}
                  </Button>
                  <Button variant="plain" onClick={() => setAddOpen(true)}>
                    ＋ {t('homework.add_one')}
                  </Button>
                </div>
              </>
            )}
          </>
        ) : (
          // 「只有照片，还没条目」是一个真实且常见的中间状态：老师只发了张黑板照，
          // 家长先拍下来挂上，条目照着图片慢慢加（设计稿 R6）。空态整个换文案。
          <Empty
            emoji={shots.length > 0 ? '🖼️' : '📋'}
            title={shots.length > 0 ? t('homework.empty_shots_title') : t('homework.empty_title')}
            action={can_write ? (
              // 稿子的空态给三条录入路径，一条都不能少
              <div className="grid gap-2">
                <Button onClick={() => router.post('/homework/preview', { text: '', date: viewed_date })}>
                  📋 {t('homework.paste_cta')}
                </Button>
                <Button variant="ghost" onClick={() => setShotSheet(true)}>
                  📷 {t('homework.shoot_board')}
                </Button>
                <Button variant="plain" onClick={() => setAddOpen(true)}>
                  ＋ {t('homework.add_one')}
                </Button>
              </div>
            ) : undefined}
          >
            {shots.length > 0 ? t('homework.empty_shots_sub') : t('homework.empty_sub')}
          </Empty>
        )
      )}

      {seg === 'due' && (
        due_items.length === 0 ? (
          <Empty emoji="🗓️" title={t('homework.due_empty_title')}>{t('homework.due_empty_sub')}</Empty>
        ) : (
          // 「要交」按**哪天要交**分组，跨天汇总 —— 「下周四回收书本」不属于任何一天的
          // 清单，它属于一个日子（设计稿 §二）。
          [...new Map(due_items.map((i) => [i.due_on!, due_items.filter((x) => x.due_on === i.due_on)]))].map(
            ([due, list]) => {
              const left = daysLeft(due)
              return (
                <Section
                  key={due}
                  title={`🗓 ${t('homework.due_group', { date: dueText(due) })}`}
                  tail={
                    // ≤1 天变琥珀（稿子的 `.soon`）：明天要交和两周后要交不该长得一样
                    <span className={left <= 1 ? 'text-amber-strong' : undefined}>
                      {left <= 0 ? t('homework.due_left_today') : t('homework.due_left', { count: left })}
                    </span>
                  }
                >
                  <List>
                    {list.map((item) => (
                      <Row
                        key={item.id}
                        title={item.body}
                        sub={`${subjectEmoji(item.subject)} ${item.subject} · ${t('homework.due_from', { date: item.on.slice(5) })}`}
                        pts={`+${item.points}`}
                        // 整行点 = 跳到布置它那天（稿子的 `›`）：要改这一条得回到那天。
                        // 这一段刻意**不复用 itemRow** —— 那里整行点是打卡，
                        // 而在这一段「打卡」和「交了」是两个不同的动作。
                        onTap={() => router.get('/homework', { date: item.on }, { preserveScroll: true })}
                        ariaLabel={t('homework.jump_aria', { body: item.body, date: item.on })}
                        right={canAct ? (
                          <button
                            type="button"
                            onClick={() => hand(item)}
                            aria-label={t('homework.hand_aria', { body: item.body })}
                            className="shrink-0 min-h-tap px-3 rounded-chip bg-green-tint text-green-strong text-xs font-bold active:bg-green active:text-white"
                          >
                            ✓ {t('homework.hand')}
                          </button>
                        ) : undefined}
                      />
                    ))}
                  </List>
                </Section>
              )
            },
          )
        )
      )}

      {seg === 'sign' && (
        sign_items.length === 0 ? (
          <Empty emoji="✍️" title={t('homework.sign_empty_title')}>{t('homework.sign_empty_sub')}</Empty>
        ) : can_write ? (
          <>
            <ReviewQueue
              title={t('homework.sign_title')}
              count={sign_items.length}
              hint={t('homework.sign_hint')}
              rows={sign_items.map((i) => ({
                id: i.id,
                title: i.body,
                // 稿子在这一段显示「📷 N 个附件，点开看」/「没拍照片」——
                // 家长睡前签名时想先看一眼那张默写纸
                sub: [
                  `${subjectEmoji(i.subject)} ${i.subject}`,
                  i.on.slice(5),
                  // 家长睡前签名时想先看一眼那张默写纸 / 听一句朗读
                  i.proofs.length > 0
                    ? [
                        i.proofs.filter((a) => a.kind === 'photo').length,
                        i.proofs.filter((a) => a.kind === 'audio').length,
                      ].map((n, idx) => (n > 0 ? `${idx === 0 ? '📷' : '🎙️'} ${n}` : '')).filter(Boolean).join(' ')
                    : t('homework.proof_none'),
                ].join(' · '),
                pts: `+${i.points}`,
                onApprove: () => sign(i),
                approveLabel: t('homework.sign_aria', { body: i.body }),
              }))}
            />
            {/* 两条以上才给批量：只有一条时「全部签了（1 条）」比行里那个 ✓ 更远。
                ⚠️ 批量签**不给 undo** —— 撤销 N 条要记住「这一批是哪 N 条」，
                服务端这边没有那个东西（稿子里靠一个整体快照回滚）。逐条撤销
                仍然可以（回「今天」段点那一行）。这是与规矩 §3 的刻意偏离。 */}
            {sign_items.length > 1 && (
              <div className="mx-3 mt-3">
                <Button
                  variant="ghost"
                  onClick={() => {
                    const count = sign_items.length
                    router.post('/homework/sign_all', {}, {
                      preserveScroll: true,
                      onSuccess: () => toast(t('homework.toast_signed_all', { count })),
                    })
                  }}
                >
                  ✍️ {t('homework.sign_all', { count: sign_items.length })}
                </Button>
              </div>
            )}
          </>
        ) : (
          // 孩子看得到自己在等什么，但签不了 —— 那正是这一步的意义
          <Section title={t('homework.sign_title')} tail={<Badge tone="amber">{sign_items.length}</Badge>}>
            <List>{sign_items.map((item) => itemRow(item, true))}</List>
          </Section>
        )
      )}

      {/* 条目详情：分值胶囊右边那个 ⋯ 打开的东西。改文字 / 分值 / 签名 / 要交的日子 / 删除。
          不给行里加第三个按钮 —— 375px 上第三个按钮会把作业正文挤成两个字一行。 */}
      <ItemDetailSheet item={detail} onClose={() => setDetail(null)} />

      <AddItemSheet
        open={addOpen}
        onClose={() => setAddOpen(false)}
        date={viewed_date}
        subjects={subjectChoices}
      />

      {/* 稿子的 `shotSheet()`：借用同一个附件面板，但标题口径不同 ——
          这里是「这一天的作业图」，不是「某一条的证据」。 */}
      <Sheet
        open={shotSheet}
        onClose={() => setShotSheet(false)}
        label={t('homework.shoot_board')}
        title={t('homework.shoot_board')}
      >
        <SheetPad>
          <p className="m-0 mb-3 text-muted text-[13px] leading-[1.5]">{t('homework.shoot_hint')}</p>
          <AttachmentPicker onPick={uploadShots} busy={uploading} />
          {uploading && <p className="mt-3 text-faint text-xs">{t('homework.photo_uploading')}</p>}
        </SheetPad>
      </Sheet>

      {/* 稿子的 `#attSheet`：打卡证据。已有的几张就在面板里显示，可以点开看。 */}
      <Sheet
        open={proofFor !== null}
        onClose={() => setProofForId(null)}
        label={t('homework.proof_title')}
        title={t('homework.proof_title')}
      >
        <SheetPad>
          <p className="m-0 mb-3 text-muted text-[13px] leading-[1.5]">{t('homework.proof_hint')}</p>
          {/* 条目级证据允许录音；天级作业图不允许（那是「拍下老师发的那张」）。 */}
          <AttachmentPicker onPick={uploadProofs} busy={uploading} allowAudio />
          {uploading && <p className="mt-3 text-faint text-xs">{t('homework.photo_uploading')}</p>}
          {/* 稿子的 `.attList` 分两段：照片走缩略图网格，录音走带播放键的行。
              两种东西的操作完全不同（看 / 听），混在一个列表里两边都别扭。 */}
          <AudioList
            items={proofFor?.proofs.filter((a) => a.kind === 'audio') ?? []}
            onDelete={can_write ? deleteProof : undefined}
          />
        </SheetPad>
        {proofFor !== null && proofFor.proofs.some((a) => a.kind === 'photo') && (
          <PhotoStrip
            photos={proofFor.proofs.filter((a) => a.kind === 'photo')}
            canWrite={false}
            onAdd={() => undefined}
            onOpen={(photo) => setViewing({ photo, kind: 'proof' })}
          />
        )}
      </Sheet>

      <PhotoViewer
        photo={viewing?.photo ?? null}
        onClose={() => setViewing(null)}
        onDelete={can_write ? deleteAttachment : undefined}
      />

      <Sheet
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        label={t('homework.settings_title')}
        title={t('homework.settings_title')}
      >
        <SettingsForm settings={settings} onDone={() => setSettingsOpen(false)} />
      </Sheet>
    </Layout>
  )
}

/** 条目详情 sheet。R8：录错能改 —— 文字 / 分值 / 签名 / 截止 / 删除。 */
function ItemDetailSheet({ item, onClose }: { item: HomeworkItemRow | null; onClose: () => void }) {
  const [body, setBody] = useState('')
  const [points, setPoints] = useState('0')
  const [needsSign, setNeedsSign] = useState(false)
  const [dueOn, setDueOn] = useState('')
  const [kind, setKind] = useState<'todo' | 'note'>('todo')
  const [confirmDelete, setConfirmDelete] = useState(false)
  // 每次换条目就把表单灌一遍。用 key 而不是 useEffect：父组件给这个组件换
  // key 之后它整个重挂，state 自然是新的（见下面 <ItemDetailSheet key=…>）。
  const [seeded, setSeeded] = useState<number | null>(null)
  if (item && seeded !== item.id) {
    setSeeded(item.id)
    setBody(item.body)
    setPoints(String(item.points))
    setNeedsSign(item.needs_signature)
    setDueOn(item.due_on ?? '')
    setKind(item.kind)
    setConfirmDelete(false)
  }

  const save = () => {
    const id = item!.id
    onClose()
    router.patch(`/homework_items/${id}`, { body, points, needs_signature: needsSign, due_on: dueOn, kind }, {
      onSuccess: () => toast(t('homework.toast_item_saved')),
    })
  }

  const remove = () => {
    const removed = item!
    onClose()
    router.delete(`/homework_items/${removed.id}`, {
      onSuccess: () => toast(t('homework.toast_item_deleted', { body: removed.body })),
    })
  }

  return (
    <Sheet open={item !== null} onClose={onClose} label={t('homework.detail_title')} title={t('homework.detail_title')}>
      <SheetPad>
        <Field label={t('homework.field_body')}>
          <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={2} className={fieldInputClass} />
        </Field>

        {/* 解析判错了在这里翻回来。翻成「要求」时分值与签名标记服务端会一起清掉。 */}
        <Field label={t('homework.field_kind')}>
          <div className="flex gap-2">
            <Chip pressed={kind === 'todo'} onClick={() => setKind('todo')}>✓ {t('homework.kind_todo')}</Chip>
            <Chip pressed={kind === 'note'} onClick={() => setKind('note')}>⚠️ {t('homework.kind_note')}</Chip>
          </div>
        </Field>

        {kind === 'todo' && (
          <>
            <Field label={t('homework.field_points')}>
              <div className="flex gap-2 flex-wrap">
                {POINTS.map((p) => (
                  <Chip key={p} pressed={points === String(p)} onClick={() => setPoints(String(p))}>{p}</Chip>
                ))}
              </div>
            </Field>
            <Field label={t('homework.field_sign')}>
              <div className="flex gap-2">
                <Chip pressed={needsSign} onClick={() => setNeedsSign(true)}>{t('homework.sign_yes')}</Chip>
                <Chip pressed={!needsSign} onClick={() => setNeedsSign(false)}>{t('homework.sign_no')}</Chip>
              </div>
            </Field>
          </>
        )}

        <Field label={t('homework.field_due')}>
          <input type="date" value={dueOn} onChange={(e) => setDueOn(e.target.value)} className={fieldInputClass} />
        </Field>

        <div className="grid gap-2">
          <Button disabled={!body.trim()} onClick={save}>{t('homework.save_item')}</Button>
          {confirmDelete ? (
            <>
              <div className="px-3.5 py-3 rounded-ctl bg-red-tint text-red-strong text-[13px] leading-[1.45]">
                {t('homework.delete_warn')}
              </div>
              <Button variant="danger" onClick={remove}>{t('homework.delete_confirm')}</Button>
              <Button variant="plain" onClick={() => setConfirmDelete(false)}>{t('people.keep')}</Button>
            </>
          ) : (
            <Button variant="plain" onClick={() => setConfirmDelete(true)}>🗑️ {t('homework.delete_item')}</Button>
          )}
        </div>
      </SheetPad>
    </Sheet>
  )
}

/**
 * 稿子的 `#addSheet`：老师只发照片、或临时想起一件事时手动加一条（设计稿 §三 次路径）。
 *
 * 学科 chips 的取值 = 当天已有的学科 + 三个常见的 + 「其它」，去重。稿子里就是这么拼的
 * （`openAdd()`）：真实的那几门课已经在单子上了，不该让人重新打一遍。
 */
function AddItemSheet({ open, onClose, date, subjects }: {
  open: boolean
  onClose: () => void
  date: string
  subjects: string[]
}) {
  const [subject, setSubject] = useState(subjects[0] ?? '')
  const [body, setBody] = useState('')
  const [points, setPoints] = useState('2')
  const [needsSign, setNeedsSign] = useState(false)

  const add = () => {
    // 存下来再关：onClose 之后 subject 的 state 还在，但 toast 的文案要的是
    // 「点下去那一刻选的是哪门课」，读闭包里这一份最省心。
    const into = subject
    onClose()
    router.post('/homework_items', {
      date, subject: into, body, points, needs_signature: needsSign,
    }, {
      onSuccess: () => {
        setBody('')
        toast(t('homework.toast_item_added', { subject: into }))
      },
    })
  }

  return (
    <Sheet open={open} onClose={onClose} label={t('homework.add_one_title')} title={t('homework.add_one_title')}>
      <SheetPad>
        <Field label={t('homework.add_subject')}>
          <div className="flex gap-2 flex-wrap">
            {subjects.map((s) => (
              <Chip key={s} pressed={subject === s} onClick={() => setSubject(s)}>{s}</Chip>
            ))}
          </div>
        </Field>

        <Field label={t('homework.add_body')}>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={3}
            placeholder={t('homework.add_body_placeholder')}
            className={fieldInputClass}
          />
        </Field>

        <Field label={t('homework.field_points')}>
          <div className="flex gap-2 flex-wrap">
            {POINTS.map((p) => (
              <Chip key={p} pressed={points === String(p)} onClick={() => setPoints(String(p))}>{p}</Chip>
            ))}
          </div>
        </Field>

        <Field label={t('homework.field_sign')}>
          <div className="flex gap-2">
            <Chip pressed={needsSign} onClick={() => setNeedsSign(true)}>{t('homework.sign_yes')}</Chip>
            <Chip pressed={!needsSign} onClick={() => setNeedsSign(false)}>{t('homework.sign_no')}</Chip>
          </div>
        </Field>

        <Button disabled={!body.trim()} onClick={add}>{t('homework.add_do')}</Button>
      </SheetPad>
    </Sheet>
  )
}

/** 每条分值 + 每日上限 + 常驻条目（设计稿 §五 / R13）。 */
function SettingsForm({ settings, onDone }: {
  settings: Props['settings']
  onDone: () => void
}) {
  const [perPoints, setPerPoints] = useState(String(settings.points_per))
  const [dailyCap, setDailyCap] = useState(settings.daily_cap === null ? '' : String(settings.daily_cap))
  const [standing, setStanding] = useState(settings.standing)

  const save = () => {
    onDone()
    router.patch('/homework/settings', { points_per: perPoints, daily_cap: dailyCap, standing }, {
      onSuccess: () => toast(t('homework.toast_settings_saved')),
    })
  }

  return (
    <SheetPad>
      {/* 稿子里这上面还有一档「怎么给分」（每条给分 / 全做完给一次）。
          原来那一版是两个 chip：per / subject —— 但记分逻辑（points.rb）
          从来没读过它，按下去什么都不会变。整档删掉了，理由见 S6 计划决定 A；
          真做「全做完给一次」要连它的新触发时机一起做（附录 Task 10）。 */}
      <Field label={t('homework.points_per_label')}>
        <div className="flex gap-2 flex-wrap">
          {[1, 2, 3, 5].map((p) => (
            <Chip key={p} pressed={perPoints === String(p)} onClick={() => setPerPoints(String(p))}>{p}</Chip>
          ))}
        </div>
      </Field>

      {/* 不设上限就会通胀：老师布置 12 条那天孩子一晚上赚出的分比固定习惯全勤
          一天还多，固定习惯那套激励会被压掉（设计稿 §五）。 */}
      <Field label={t('homework.cap_label')}>
        <div className="flex gap-2 flex-wrap">
          {['', '8', '12', '20'].map((c) => (
            <Chip key={c || 'off'} pressed={dailyCap === c} onClick={() => setDailyCap(c)}>
              {c === '' ? t('homework.cap_off') : c}
            </Chip>
          ))}
        </div>
      </Field>

      <Field label={t('homework.standing_label')}>
        <textarea
          value={standing}
          onChange={(e) => setStanding(e.target.value)}
          rows={3}
          placeholder={t('homework.standing_placeholder')}
          className={fieldInputClass}
        />
        <span className="block mt-1.5 text-faint text-xs">{t('homework.standing_hint')}</span>
      </Field>

      <Button onClick={save}>{t('homework.save_settings')}</Button>
    </SheetPad>
  )
}
